import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { FC, ReactNode } from 'react';

export interface PostActionItem {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /** Destructive action (e.g. delete) — rendered in the error colour. */
  danger?: boolean;
}

interface PostActionsMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  items: PostActionItem[];
}

const FONT = 'Figtree, sans-serif';

/**
 * Actions menu for a post card. On desktop it's a compact rounded popover
 * anchored to the "⋯" button; on phones it's a bottom sheet with roomy,
 * tap-friendly rows — much nicer than a tiny anchored menu on mobile.
 */
export const PostActionsMenu: FC<PostActionsMenuProps> = ({ anchorEl, open, onClose, items }) => {
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));

  // Close first, then run — so the sheet/menu animates out cleanly.
  const run = (item: PostActionItem) => {
    onClose();
    item.onClick();
  };

  if (isBelowMd) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => undefined}
        disableSwipeToOpen
        PaperProps={{
          sx: { borderRadius: '20px 20px 0 0', pb: 'calc(8px + env(safe-area-inset-bottom))' },
        }}
      >
        {/* Grab handle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10px', pb: '6px' }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#e2dddb' }} />
        </Box>
        <Box sx={{ px: 1.25, pt: 0.5 }}>
          {items.map(item => (
            <Box
              key={item.id}
              component="button"
              type="button"
              onClick={() => run(item)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
                border: 'none',
                cursor: 'pointer',
                bgcolor: 'transparent',
                borderRadius: '12px',
                px: 1.25,
                py: 1.25,
                textAlign: 'left',
                color: item.danger ? 'error.main' : '#2a2a35',
                '&:active': { bgcolor: '#f4f2f1' },
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  flexShrink: 0,
                  borderRadius: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: item.danger ? '#fdecec' : '#f4f2f1',
                  color: 'inherit',
                  fontSize: 18,
                }}
              >
                {item.icon}
              </Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: 'inherit' }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            minWidth: 190,
            borderRadius: '12px',
            border: '1px solid #f0ecea',
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
            p: 0.5,
          },
        },
      }}
      MenuListProps={{ sx: { py: 0 } }}
    >
      {items.map(item => (
        <MenuItem
          key={item.id}
          onClick={() => run(item)}
          sx={{
            borderRadius: '8px',
            px: 1,
            py: 0.85,
            gap: 0.5,
            fontFamily: FONT,
            fontSize: 13.5,
            fontWeight: 600,
            color: item.danger ? 'error.main' : '#2a2a35',
            '&:hover': { bgcolor: item.danger ? '#fdecec' : '#f6f4f3' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, color: 'inherit', '& svg': { fontSize: 18 } }}>{item.icon}</ListItemIcon>
          {item.label}
        </MenuItem>
      ))}
    </Menu>
  );
};
